import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from './api'
import { useAuthStore } from './auth'
import logger from '@/utils/logger'

export interface ChatSession {
  id: string
  client_id: number
  session_id: string
  visitor_name: string | null
  visitor_email: string | null
  agent_id: number | null
  agent_first_name?: string | null
  agent_last_name?: string | null
  client_name?: string
  status: 'queued' | 'active' | 'ended'
  started_at: string | null
  ended_at: string | null
  metadata: Record<string, any> | null
  created_at: string
}

export interface ChatMessage {
  id: string
  session_id: string
  sender_type: 'visitor' | 'agent' | 'system'
  sender_id: number | null
  sender_name?: string
  message: string
  created_at: string
}

export const useChatStore = defineStore('chat', () => {
  const sessions = ref<ChatSession[]>([])
  const selectedSessionId = ref<string | null>(null)
  const messages = ref<ChatMessage[]>([])
  const isOpen = ref(false)
  const loading = ref(false)
  const messagesLoading = ref(false)

  const queuedSessions = computed(() => sessions.value.filter(s => s.status === 'queued'))
  const activeSessions = computed(() => sessions.value.filter(s => s.status === 'active'))

  const selectedSession = computed(() =>
    sessions.value.find(s => s.id === selectedSessionId.value) || null
  )

  const unreadCount = computed(() => queuedSessions.value.length)

  const mySessions = computed(() => {
    const authStore = useAuthStore()
    const userId = authStore.user?.id
    if (!userId) return []
    return activeSessions.value.filter(s => s.agent_id === userId)
  })

  async function fetchSessions() {
    loading.value = true
    try {
      const res = await api.get('/chat/sessions')
      sessions.value = res.data.data || []
    } catch (err) {
      logger.error('Failed to fetch chat sessions', err)
    } finally {
      loading.value = false
    }
  }

  async function fetchMessages(sessionId: string) {
    messagesLoading.value = true
    try {
      const res = await api.get(`/chat/sessions/${sessionId}`)
      messages.value = res.data.data?.messages || []
    } catch (err) {
      logger.error('Failed to fetch chat messages', err)
    } finally {
      messagesLoading.value = false
    }
  }

  async function selectSession(sessionId: string) {
    selectedSessionId.value = sessionId
    await fetchMessages(sessionId)
  }

  async function claimSession(sessionId: string) {
    try {
      await api.put(`/chat/sessions/${sessionId}/assign`)
      await fetchSessions()
      await selectSession(sessionId)
    } catch (err) {
      logger.error('Failed to claim chat session', err)
    }
  }

  async function sendMessage(sessionId: string, message: string) {
    try {
      const res = await api.post(`/chat/sessions/${sessionId}/messages`, {
        message,
        sender_type: 'agent',
      })
      if (res.data.data) {
        messages.value.push(res.data.data)
      }
    } catch (err) {
      logger.error('Failed to send chat message', err)
    }
  }

  async function endSession(sessionId: string) {
    try {
      await api.put(`/chat/sessions/${sessionId}/end`)
      // Remove from list or refresh
      await fetchSessions()
      if (selectedSessionId.value === sessionId) {
        selectedSessionId.value = null
        messages.value = []
      }
    } catch (err) {
      logger.error('Failed to end chat session', err)
    }
  }

  function addIncomingMessage(msg: ChatMessage) {
    if (msg.session_id === selectedSessionId.value) {
      // Avoid duplicates
      if (!messages.value.find(m => m.id === msg.id)) {
        messages.value.push(msg)
      }
    }
  }

  function updateSessionFromEvent(session: Partial<ChatSession> & { id: string }) {
    const idx = sessions.value.findIndex(s => s.id === session.id)
    if (idx >= 0) {
      sessions.value[idx] = { ...sessions.value[idx], ...session }
    } else if (session.status && session.status !== 'ended') {
      sessions.value.push(session as ChatSession)
    }
    // Remove ended sessions
    if (session.status === 'ended') {
      sessions.value = sessions.value.filter(s => s.id !== session.id)
    }
  }

  function toggle() {
    isOpen.value = !isOpen.value
  }

  return {
    sessions,
    selectedSessionId,
    messages,
    isOpen,
    loading,
    messagesLoading,
    queuedSessions,
    activeSessions,
    selectedSession,
    unreadCount,
    mySessions,
    fetchSessions,
    fetchMessages,
    selectSession,
    claimSession,
    sendMessage,
    endSession,
    addIncomingMessage,
    updateSessionFromEvent,
    toggle,
  }
})
